<template>
  <b-layout :colors="$site.colors" :header-title="pass?.name || ''" :header-back-route="homeRoute" :breadcrumbs="breadcrumbs">
    <b-container class="mt-12">
      <b-summary>
        <template #summary>
          <b-card class="text-secondary">
            <img v-if="passImage" :src="passImage" class="mb-4 rounded">

            <div v-if="details.toName" class="mb-1">
              <strong>{{ details.toName }},</strong>
            </div>
            {{ details.message }}
            <div v-if="details.fromName" class="mt-3">
              <strong>{{ details.fromName }}</strong>
            </div>
            <hr class="mt-4">
            <h3 class="font-semibold text-xl mt-6 mb-6">
              {{ pass.name }}
            </h3>
            <h6 class="text-xs font-weight-light mb-2">
              {{ $site.location.title }}
            </h6>
          </b-card>
        </template>
        <div>
          <b-errors ref="errors" :errors="errors" />

          <b-card :title="$t('pages.gift.pass.index.nameGiftPass',{name: pass.name})" class="mb-6">
            <UFormGroup v-if="pass.information" :label="$t('pages.gift.pass.index.description')+':'" class="mb-6">
              <b-card>
                <span v-html="formatHtml(pass.information)" class="text-secondary" />
              </b-card>
            </UFormGroup>

            <UFormGroup :label="$t('pages.gift.pass.index.to')+':'" class="mb-6">
              <UFormGroup size="sm" class="mb-1">
                <UInput v-model="details.toName" :placeholder="$t('pages.gift.pass.index.namePlaceholder')" size="lg" class="mb-4" :ui="{ leading: { padding: { lg: 'ps-20'}}}">
                  <template #leading>
                    <p class="text-secondary/50 mr-3 pr-3 border-r border-secondary">
                      {{ $t('general.name') }}
                    </p>
                  </template>
                </UInput>
              </UFormGroup>

              <UFormGroup size="sm">
                <UInput v-model.trim="details.toEmail" :placeholder="$t('pages.gift.pass.index.emailPlaceholder')" size="lg" :ui="{ leading: { padding: { lg: 'ps-20'}}}">
                  <template #leading>
                    <p class="text-secondary/50 mr-3 pr-3 border-r border-secondary">
                      {{ $t('general.email') }}
                    </p>
                  </template>
                </UInput>
              </UFormGroup>
            </UFormGroup>

            <UFormGroup :label="$t('pages.gift.pass.index.from')+':'" size="sm" class="mb-6">
              <UInput v-model="details.fromName" :placeholder="$t('pages.gift.pass.index.fromPlaceholder')" size="lg" class="mb-4" :ui="{ leading: { padding: { lg: 'ps-20'}}}">
                <template #leading>
                  <p class="text-secondary/50 mr-3 pr-3 border-r border-secondary">
                    {{ $t('general.name') }}
                  </p>
                </template>
              </UInput>
            </UFormGroup>

            <UFormGroup :label="$t('pages.gift.pass.index.message')+':'" class="mb-6">
              <UTextarea
                id="textarea1"
                v-model="details.message"
                :placeholder="$t('pages.gift.pass.index.messagePlaceholder')"
                rows="3"
                max-rows="6"
                size="lg"
              />
            </UFormGroup>

            <UFormGroup :label="$t('pages.gift.pass.index.deliveryDate')+':'">
              <client-only>
                <DateField v-model="details.deliveryDate" :min-date="new Date()" :placeholder="$t('pages.gift.pass.index.deliveryDatePlaceholder')" augmenter="ymd" size="lg" />
              </client-only>
            </UFormGroup>
          </b-card>

          <b-button class="w-full" variant="action" @click="next">
            {{ $t('pages.gift.pass.index.giftPassPrice',{name: pass.name, price: pass.priceLabel}) }} »
          </b-button>
        </div>
      </b-summary>
    </b-container>
  </b-layout>
</template>

<script>
import DateField from '@/components/fields/Date'
import Utils from '@/utils'

definePageMeta({
  tabs: ['giftCard'],
  middleware: ['redirects']
});

export default defineNuxtComponent({
  components: {
    DateField
  },
  async asyncData () {
    const route = useRoute()
    const { $api } = useNuxtApp()
    const query = route.query

    if (query.id) {
      try  {
        const response = await $api.pass.pass(query.id)

        if (!response.pass) {
          navigateTo({ path: '/gift' })
        }

        return {
          pass: response.pass
        }
      } catch (e) {
        console.error(e)

        navigateTo({ path: '/gift' })
      }
    }
  },
  data () {
    return {
      details: {
        toName: '',
        toEmail: '',
        fromName: '',
        message: '',
        deliveryDate: null
      },
      errors: []
    }
  },
  computed: {
    breadcrumbs () {
      return [
        { text: this.$site.tabs.giftCard.name, to: { path: '/gift' } },
        { text: this.pass.name, active: true }
      ]
    },
    title () {
      return this.$site.tabs.giftCard.name
    },
    info () {
      return this.$site.tabs.getInfo('pass')
    },
    homeRoute () {
      return this.$store.getters.homeRoute || { path: '/' }
    },
    passImage () {
      if (this.pass.images) {
        return this.pass.images.large
      } else if (this.$site.images.pass) {
        return this.$site.images.pass.large
      }
      return null
    }
  },
  created () {
    this.details.message = 'I hope you enjoy this ' + this.pass?.name + '!'
  },
  methods: {
    formatHtml (text) {
      return Utils.text.formatHtml(text)
    },
    async next () {
      this.errors = []

      this.details.toEmail = Utils.validation.sanitizeEmail(this.details.toEmail)

      if (!this.details.toName) { this.errors.push(this.$t('pages.gift.pass.index.toNameError')) }
      if (!this.details.toEmail) { this.errors.push(this.$t('pages.gift.pass.index.toEmailError')) }
      if (!this.details.fromName) { this.errors.push(this.$t('pages.gift.pass.index.fromNameError')) }
      if (!this.details.message) { this.errors.push(this.$t('pages.gift.pass.index.messageError')) }

      if (!Utils.validation.email(this.details.toEmail)) { this.errors.push(this.$t('pages.gift.pass.index.validEmailError')) }

      if (this.errors.length) {
        this.$nextTick(() => {
          // this.scrollTo(this.$refs.errors)
          this.$refs?.errors?.$el?.scrollIntoView({ behavior: 'smooth' })
        })
        
        return
      }

      await this.$store.dispatch('gift/setPassDetails', { pass: this.pass, toName: this.details.toName, toEmail: this.details.toEmail, fromName: this.details.fromName, message: this.details.message, deliveryDate: this.details.deliveryDate })

      const urlQuery = this.$store.getters['gift/urlQuery']
      this.$router.push({ path: '/gift/pass/customer', query: urlQuery })
    }
  }
})
</script>

<style>
.bookapp-vc-bg-accent-high {
  background-color: var(--bookapp-bg-action)
}
</style>
